import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@taiga-platform/ui";
import { Callout, Details } from "../components";

export function Team() {
  return (
    <>
      <h1>The Team</h1>

      <h2>We have done this before.</h2>
      <p>
        The four founders of TAIGA are not a group of strangers who met at a
        hackathon. We built a company together — from zero to EUR 30M+ in
        revenue, 150 people, and a private equity exit — bootstrapped,
        profitable the entire way. Over a decade of shared history. That is
        the strongest signal we can offer: we know how to build, scale, and
        exit a technology company, and we know how to do it together.
      </p>

      <hr />

      <h2>Cloud2 track record</h2>
      <p>
        Before TAIGA, all four founders built Cloud2 together. Not a side
        project. Not a brief overlap. They built one of Finland's
        fastest-growing cloud and security firms from near-zero to EUR 30M+ in
        revenue and 150 people — bootstrapped, consistently profitable,
        without a single euro of external capital.
      </p>

      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Revenue</td>
            <td>EUR 30M+</td>
          </tr>
          <tr>
            <td>Team</td>
            <td>150 FTEs at peak</td>
          </tr>
          <tr>
            <td>Funding</td>
            <td>Bootstrapped — zero external capital</td>
          </tr>
          <tr>
            <td>Profitability</td>
            <td>Consistent throughout growth</td>
          </tr>
          <tr>
            <td>Exit</td>
            <td>Minority PE exit (CapMan Growth)</td>
          </tr>
        </tbody>
      </table>

      <p>
        Henri led as CEO. Mikko served as CTO for 8 years (joining as employee
        #9), also holding dual CISO and CIO roles. Teemu and Anton ran the CTO
        Office together, owning architecture, AI strategy, and platform
        engineering.
      </p>

      <h3>What they built</h3>
      <p>
        <strong>M&amp;A on both sides.</strong> The team executed the Keycore
        acquisition (2023) — technical and operational due diligence,
        integration of a Danish competitor into Cloud2's operations. On the
        seller side, they led the CapMan Growth investment process (2022),
        ensuring the valuation reflected the company's true technical and
        commercial strength.
      </p>
      <p>
        <strong>Two SaaS products.</strong> Cloud2 was not just a consulting
        shop. The team launched two SaaS products from concept to market — a
        transition from services revenue to product revenue that most
        consulting companies never make. This is directly relevant to TAIGA:
        the same team is making the same transition again, at a larger scale.
      </p>
      <p>
        <strong>ISO 27001 certification.</strong> The team built and
        maintained the Information Security Management System, achieved ISO
        27001 certification, and passed numerous client security assessments
        over multiple years. This is why TAIGA's security posture is
        enterprise-grade from day one — the team has done it before and knows
        exactly what it takes.
      </p>
      <p>
        <strong>Strategic partnerships.</strong> AWS Premier Partner status.
        Microsoft and Google strategic partnerships. The team understands
        hyperscaler ecosystems from the inside — how they sell, how they
        partner, and where they leave gaps that startups can fill.
      </p>

      <h3>Why we left</h3>
      <p>
        Cloud2 was the best professional experience of our lives. We built
        something real — 150 people, EUR 30M+ revenue, profitable every year,
        PE investment, international expansion. We are proud of every part of
        it.
      </p>
      <p>
        But after the CapMan Growth investment, the company entered a new
        phase. EBITDA optimization. Process maturation. Corporate governance.
        All the right things for Cloud2's next chapter — but not the things
        that make our hearts burn. We are builders. We want to create
        something new, not optimize something mature.
      </p>
      <p>
        The honest truth: once you have seen what AI agents can do — and once
        you understand that nobody is governing them — you cannot unsee it.
        The opportunity was too big and too urgent to pursue as a side
        project inside a consulting company. It needed its own company, its
        own capital, and our full attention.
      </p>
      <p>
        We left as good leavers. The founders who retained Cloud2 equity
        remain shareholders and informal advisors. We talk regularly. There
        is no non-compete on TAIGA's business — Cloud2 is a cloud consulting
        services company, TAIGA is an AI governance platform company.
        Different markets, different customers, different business models.
        The relationship is warm, supportive, and clean.
      </p>

      <Callout variant="tip" title="For investors asking: will they leave TAIGA too?">
        <p>
          Cloud2 was a consulting company. The founders built it to EUR 30M+
          and stayed for 8 years. They left because the company matured
          beyond the building phase — not because they lost interest in the
          mission.
        </p>
        <p>
          TAIGA is a venture-backed product company in a category that is
          forming right now. The building phase here is measured in decades,
          not years. There is no "optimization phase" coming anytime soon.
          These founders left a mature company specifically to find something
          worth building for the next 10+ years. They found it.
        </p>
        <p>
          The risk of early departure is low. The risk of not being ambitious
          enough is zero. And of course, standard 4-year cliff and vesting is
          already in place.
        </p>
      </Callout>

      <hr />

      <h2>The working history</h2>
      <p>
        Most founding teams meet months before incorporating. We have been
        building together for a decade.
      </p>

      <table>
        <thead>
          <tr>
            <th>Pair</th>
            <th>History</th>
            <th>Duration</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Mikko &amp; Henri</td>
            <td>2 companies together</td>
            <td>9 years</td>
          </tr>
          <tr>
            <td>Mikko &amp; Anton</td>
            <td>2 companies together</td>
            <td>10 years</td>
          </tr>
          <tr>
            <td>Mikko &amp; Teemu</td>
            <td>1 company together</td>
            <td>5 years</td>
          </tr>
          <tr>
            <td>Teemu &amp; Anton</td>
            <td>CTO Office at Cloud2</td>
            <td>5+ years</td>
          </tr>
        </tbody>
      </table>

      <p>80+ years combined tech leadership across the founding team. 5 exits combined.</p>
      <p>
        This is not a team that needs to figure out how to work together. The
        working relationships, the trust, and the decision-making patterns
        are already there.
      </p>

      <hr />

      <h2>The founders</h2>
      <p>
        <strong>Mikko "Mixu" Laakkonen — CEO.</strong> Vision, Sales,
        Fundraising. Learned to code at 8, founded his first company at 20,
        15+ years of tech leadership. The driving force behind TAIGA.
      </p>
      <p>
        <strong>Teemu Ijas — CTO.</strong> Tech Strategy, Architecture, AI
        Core. Architect of the TAIGA Engine. Deep expertise in cloud
        infrastructure, distributed systems, and AI/Platform Engineering
        integration.
      </p>
      <p>
        <strong>Henri "Henkka" Gronlund — Chair &amp; CCO.</strong> Enterprise
        Growth. Chamber of Commerce CEO of the Year 2020. Core strength in
        combining strategy, culture, and technology into sustainable growth.
      </p>
      <p>
        <strong>Anton Floor — CAIO.</strong> Product, Customer Operations.
        Cloud and AI pioneer. Aligns autonomous agents with real business
        objectives, governance, and measurable outcomes.
      </p>
      <p>Equal founder equity split. Standard 4-year vesting, 1-year cliff.</p>

      <table>
        <thead>
          <tr>
            <th>Founder</th>
            <th>LinkedIn</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Mikko Laakkonen</td>
            <td>
              <a
                href="https://www.linkedin.com/in/laakkonenmikko/"
                target="_blank"
                rel="noopener noreferrer"
              >
                linkedin.com/in/laakkonenmikko
              </a>
            </td>
          </tr>
          <tr>
            <td>Teemu Ijas</td>
            <td>
              <a
                href="https://www.linkedin.com/in/teemu-ijas/"
                target="_blank"
                rel="noopener noreferrer"
              >
                linkedin.com/in/teemu-ijas
              </a>
            </td>
          </tr>
          <tr>
            <td>Henri Gronlund</td>
            <td>
              <a
                href="https://www.linkedin.com/in/henri-gr%C3%B6nlund-47994a80/"
                target="_blank"
                rel="noopener noreferrer"
              >
                linkedin.com/in/henri-gronlund
              </a>
            </td>
          </tr>
          <tr>
            <td>Anton Floor</td>
            <td>
              <a
                href="https://www.linkedin.com/in/cloudhero/"
                target="_blank"
                rel="noopener noreferrer"
              >
                linkedin.com/in/cloudhero
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>The CEO's story</h2>
      <p>
        Mikko learned to code at 8 — modifying game ROMs in a hex editor to
        beat his school friends. By 12, he had built a CRM system in C++ and
        SQL for his school business. At 20, he founded his first company — a
        logistics business.
      </p>
      <p>
        At 22, the Lehman Brothers financial crisis wiped it out. The company
        had grown on leveraged debt with personal guarantees. When customers
        shut down overnight, everything collapsed. Mikko was personally
        liable for the debts. It took nearly 10 years to pay back every
        single cent.
      </p>
      <p>
        During those years, he ground through multiple jobs while building a
        career in tech — driven by a fierce determination to understand
        business deeply enough that it would never happen again. That grind
        took him through Nebula (cloud consulting), Atea (cloud services
        leadership), and finally Cloud2, where he joined as employee #9 and
        served as CTO for 8 years. He held dual CISO and CIO roles, led
        M&amp;A on both sides (Keycore acquisition as buyer, CapMan Growth
        investment as seller), and completed an Executive MBA at Aalto
        University (2022-2024).
      </p>

      <Callout variant="quote">
        The bankruptcy taught resilience at a fundamental level. Not
        LinkedIn-resilience. The kind where you spend a decade paying back
        debts you could have walked away from, because you decided that is
        not who you are.
      </Callout>

      <p>
        This is the person leading TAIGA. Someone who has already survived
        the worst thing that can happen in business — and came out of it with
        the skills, the scars, and the determination to build something that
        lasts.
      </p>

      <hr />

      <h2>Leadership evolution</h2>
      <p>
        Henri is handing the CEO reins to Mikko — the technical visionary for
        an AI-first company — while Henri moves to Chair &amp; CCO, leading
        enterprise sales and strategic partnerships. This is a deliberate
        shift. TAIGA is a product company, not a services company. Its
        leadership structure reflects that.
      </p>

      <hr />

      <h2>Why this team for this problem</h2>

      <Callout variant="tip" title="Why This Team for This Problem">
        <ol>
          <li>
            <strong>Governance is the feature, not a checkbox</strong> — the
            team built and maintained ISO 27001 at Cloud2. They know
            enterprise compliance from the inside.
          </li>
          <li>
            <strong>Full lifecycle experience</strong> — not just code
            generation, but specification, delivery, operations, and
            compliance across multiple enterprise customers.
          </li>
          <li>
            <strong>Enterprise DNA</strong> — 80+ years building and selling
            enterprise software. They know how regulated companies buy.
          </li>
          <li>
            <strong>EU-first</strong> — data residency and compliance are
            foundational, not bolted on. Finnish pragmatism applied to AI
            governance.
          </li>
          <li>
            <strong>AI-native from day one</strong> — TAIGA uses its own
            platform to build itself. The team is its own first customer.
          </li>
        </ol>
      </Callout>

      <hr />

      <h2>Advisor Angels</h2>

      <Details variant="info" title="Advisor Angels — Five Personal Investors">
        <p>
          Five experienced operators have invested personally in TAIGA. These
          are not passive advisors — they have put their own money in and are
          actively engaged.
        </p>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Profile</TableHead>
              <TableHead>Background</TableHead>
              <TableHead>Value to TAIGA</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="font-medium">Former CEO of a Nordic cloud/hosting company</TableCell>
              <TableCell>
                Scaled to EUR 35M+ ARR, exited for EUR 165M+ to a Nordic telco
              </TableCell>
              <TableCell>
                Enterprise scaling playbook, 50+ enterprise client network
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">European VC and deep-tech founder</TableCell>
              <TableCell>
                Co-founded a cleantech company capturing 15% EU market share,
                Techstars network, portfolio of 12+ companies
              </TableCell>
              <TableCell>European VC network, warm introductions to funds</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Finnish hypergrowth operator</TableCell>
              <TableCell>
                Built a staffing platform from zero to EUR 100M+ revenue in
                record time
              </TableCell>
              <TableCell>
                One of Finland's fastest scaling stories, operational
                excellence
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Enterprise SaaS executive</TableCell>
              <TableCell>
                Multiple CEO/CxO roles across European enterprise software
                companies
              </TableCell>
              <TableCell>Enterprise SaaS go-to-market across Europe</TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="font-medium">Serial angel investor and mentor</TableCell>
              <TableCell>
                20+ years of hands-on angel investing across Nordic startups
              </TableCell>
              <TableCell>Pattern recognition across ventures, GTM advisory</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <p>
          All advisor angels have invested significantly and personally.
          Additional board member and advisor candidates identified,
          conversations ongoing.
        </p>
      </Details>

      <hr />

      <h2>Board of Directors</h2>
      <table>
        <thead>
          <tr>
            <th>Role</th>
            <th>Member</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Chair</td>
            <td>Henri Gronlund (Founder)</td>
          </tr>
          <tr>
            <td>Board Member</td>
            <td>Mikko Laakkonen (Founder)</td>
          </tr>
          <tr>
            <td>Board Member</td>
            <td>TBD — Independent / Investor</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Hiring philosophy</h2>
      <p>
        Currently recruiting the first hire: a full-stack AI builder to
        accelerate platform development velocity. The bar is high.
      </p>
      <p>
        TAIGA maximizes growth with a minimal team of industry superstars.
        The hiring framework:
      </p>
      <ol>
        <li>
          <strong>First, eliminate the need.</strong> Before opening any role,
          ask: can AI handle this? TAIGA's own platform, agents, and
          automation cover ground that would traditionally require multiple
          hires.
        </li>
        <li>
          <strong>If we still need a head, hire an industry leader.</strong>{" "}
          Every hire should be someone who, combined with AI capabilities,
          delivers the execution power of a full team. One exceptional person
          with strong AI skills outperforms five average ones.
        </li>
        <li>
          <strong>Compute over headcount.</strong> The architecture is
          designed so that compute scales where headcount traditionally
          would. Fewer people, more leverage.
        </li>
      </ol>

      <p>
        This is not about being cheap. It is about building the kind of
        company where every person has outsized impact — and where the team
        composition itself demonstrates the thesis that AI changes how
        companies operate.
      </p>

      <Callout variant="note" title="Case in point: CISO/CIO replaced by AI">
        <p>
          A traditional startup at this stage would hire a CISO or a CIO — or
          both. TAIGA did not. Instead, the team built an ISO 27001-compliant
          Information Security Management System from scratch — fully
          automated, fully governed, fully AI-driven. It covers development
          practices, operations, security policies, risk management, and
          compliance monitoring. No human CISO required.
        </p>
        <p>
          The result: enterprise-grade security posture that passes vendor
          assessments, maintained by agents instead of a six-figure hire.
          This is not a future vision. It is running today. And it is a live
          demonstration of what TAIGA sells — governed AI replacing expensive
          human overhead without sacrificing quality or compliance.
        </p>
      </Callout>

      <h3>Technical knowledge distribution</h3>
      <p>
        The architecture is fully documented. All infrastructure is
        version-controlled as code (Terraform). CI/CD pipelines, security
        configurations, and deployment processes are automated and
        reproducible. The platform is built on standard open-source
        components (React, Fastify, PostgreSQL, Terraform) — not proprietary
        black boxes that only one person understands.
      </p>
      <p>
        The first hire (full-stack AI builder) provides additional redundancy
        on the platform engineering side. The goal is that no single person
        is irreplaceable on any technical component — not because we distrust
        the team, but because that is how you build enterprise-grade systems
        that customers can rely on.
      </p>

      <hr />

      <h2>Culture</h2>
      <p>
        We practice what we sell. TAIGA uses its own platform, its own
        governance, its own agent workflows to build TAIGA. Every piece of
        infrastructure we build for ourselves becomes a feature we sell to
        customers.
      </p>

      <p>
        <strong>Core principles:</strong>
      </p>
      <ul>
        <li>
          <strong>Governance is the product</strong> — security, compliance,
          and audit trails are what customers buy. Every design decision
          makes governance easier, not harder.
        </li>
        <li>
          <strong>Ship and learn</strong> — perfect is the enemy of shipped.
          But never ship something unsafe. Speed does not override security.
        </li>
        <li>
          <strong>Simple until proven otherwise</strong> — start with the
          simplest thing that works. A team of 4 cannot maintain what a team
          of 40 built.
        </li>
        <li>
          <strong>Automate the toil</strong> — if you are doing something
          manually more than twice, automate it. If you cannot automate it,
          document it.
        </li>
        <li>
          <strong>Documentation is a feature</strong> — if it is not
          documented, it does not exist. Write for the person who joins next
          week. Write for the AI agent that needs context at 3am.
        </li>
        <li>
          <strong>Own the outcome</strong> — we sell outcomes, not tools. "It
          works on my machine" is not a statement. "It works for the
          customer" is.
        </li>
      </ul>

      <Callout variant="quote">
        In the age of AI, world-class quality costs the same as low quality.
        We choose world-class.
      </Callout>
    </>
  );
}
